import type { SimpleMarketplaceResponse } from '@yoop/server-model/marketplace';
import { ServerMarketplaceType } from '@yoop/server-model/marketplace';
import {
  Chance,
  ServerOfferStatus,
  ServerOfferSubStatus,
  type SimpleOfferResponse,
} from '@yoop/server-model/token-info';
import { datetime } from '@yoop/datetime';
import type { MyYoopOffers } from '@yoop/whitelabel-data';
import { OfferChance, OfferStatus } from '@yoop/whitelabel-data';
import isNil from 'ramda/src/isNil';

export const requestedOffersInfoFrom = (marketplace: SimpleMarketplaceResponse) => {
  const offers: MyYoopOffers[] = [];
  let canSubmitMoreOffers = false;
  const {
    id: marketplaceId,
    type,
    offerGroupInformation,
    parentMarketplaceId,
    userTicketLimitRemaining,
    endDateTime,
  } = marketplace;
  if (
    (type !== ServerMarketplaceType.AUCTION && type !== ServerMarketplaceType.DRAW) ||
    !isNil(parentMarketplaceId)
  ) {
    return { offers, canSubmitMoreOffers };
  }
  const marketplaceEnd = endDateTime ? datetime.parse(endDateTime) : undefined;
  if (marketplaceEnd && datetime.isPast(marketplaceEnd)) {
    offerGroupInformation?.forEach(({ id: offerGroupId, description, activeOffer }) => {
      const offer = finishedRequestedOfferFrom({
        description,
        marketplaceType: type,
        activeOffer,
        marketplaceId,
        offerGroupId,
      });
      if (!isNil(offer)) {
        offers.push(offer);
      }
    });
  } else {
    offerGroupInformation?.forEach(({ id: offerGroupId, description, activeOffer }) => {
      if (isNil(activeOffer) || activeOffer.offerStatus !== ServerOfferStatus.DEFAULT) {
        //active marketplace might have lost offers in case of fraud, etc.
        return;
      }
      canSubmitMoreOffers = canSubmitMoreOffers || (userTicketLimitRemaining ?? 0) > 0;
      offers.push({
        id: parseInt(activeOffer.id),
        offerGroupId: parseInt(offerGroupId),
        price: activeOffer.amount ?? 0,
        transactingToolId: parseInt(marketplaceId),
        description,
        yoopCount: activeOffer.numberOfTokens ?? 0,
        status: OfferStatus.Active,
        chance: type === ServerMarketplaceType.AUCTION ? chanceFrom(activeOffer.chance) : undefined,
      });
    });
  }
  return { offers, canSubmitMoreOffers };
};

const finishedRequestedOfferFrom = ({
  activeOffer,
  offerGroupId,
  marketplaceId,
  marketplaceType,
  description,
}: {
  activeOffer: SimpleOfferResponse | undefined;
  marketplaceId: string;
  offerGroupId: string;
  description: string;
  marketplaceType: ServerMarketplaceType;
}) => {
  if (!activeOffer || activeOffer.offerStatus === ServerOfferStatus.WON_TOKEN_ASSIGNED) {
    return undefined;
  }

  let actionEndTime: string | undefined;
  let status: OfferStatus = OfferStatus.PendingResult;
  switch (activeOffer.offerStatus) {
    case ServerOfferStatus.LOST:
      status = OfferStatus.Lost;
      break;
    case ServerOfferStatus.WON_MANUAL_PAYMENT_REQUIRED:
      actionEndTime = activeOffer.retryEndTime;
      if (activeOffer.offerSubStatus === ServerOfferSubStatus.AUTO_PAYMENT_FAILED) {
        status = OfferStatus.PaymentFailed;
      } else {
        status = OfferStatus.PendingPayment;
      }
      break;
    case ServerOfferStatus.WON_PAYMENT_SUCCESSFUL:
      actionEndTime = activeOffer.retryEndTime;
      status = OfferStatus.PendingClaim;
      break;
    default:
  }
  return {
    id: parseInt(activeOffer.id),
    offerGroupId: parseInt(offerGroupId),
    price: activeOffer.amount ?? 0,
    transactingToolId: parseInt(marketplaceId),
    description,
    yoopCount: activeOffer.numberOfTokens ?? 0,
    actionEndTime,
    status,
    chance:
      marketplaceType === ServerMarketplaceType.AUCTION
        ? chanceFrom(activeOffer.chance)
        : undefined,
  };
};

const chanceFrom = (chance: Chance | undefined): OfferChance | undefined => {
  switch (chance) {
    case Chance.OK:
      return OfferChance.Ok;
    case Chance.GOOD:
      return OfferChance.Good;
    case Chance.GREAT:
      return OfferChance.Great;
    case Chance.NEGLIGIBLE:
      return OfferChance.Negligible;
    case Chance.POOR:
      return OfferChance.Poor;
  }
  return undefined;
};
